import {httpsCallable} from "firebase/functions";
// @ts-ignore -- "FirebaseUtils" is resolved by the web bundler, not by tsc
import {functions, isAndroid} from "FirebaseUtils";
import {META_CUSTOM_EVENTS, META_PIXEL_ID, META_STANDARD_EVENTS, buildFbc, readCookie} from "utils/metaCapi.js";
import type {CapiCustomData, MetaEventName} from "utils/metaCapi.js";
import IdGeneratorUtil from "utils/IdGeneratorUtil.js";

const FBCLID_STORAGE_KEY = "meta_fbclid";
const CHECKOUT_STORAGE_KEY = "meta_onboarding_checkout";
const PURCHASE_FIRED_KEY = "meta_onboarding_purchase_fired";
const PIXEL_SCRIPT = "https://connect.facebook.net/en_US/fbevents.js";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: Fbq;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export type RememberedCheckout = {
  value: number;
  currency: string;
  contentName?: string;
};

const firedOnce = new Set<string>();
let pixelReady = false;

const safeStorage = {
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Private mode / quota — tracking still works without persistence.
    }
  }
};

const captureFbclid = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  if (fbclid) {
    safeStorage.set(FBCLID_STORAGE_KEY, fbclid);
  }
};

const readClickIds = (): {fbp?: string; fbc?: string} => {
  if (typeof document === "undefined") {
    return {};
  }
  const fbp = readCookie("_fbp", document.cookie);
  let fbc = readCookie("_fbc", document.cookie);
  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid") || safeStorage.get(FBCLID_STORAGE_KEY);
    if (fbclid) {
      fbc = buildFbc(fbclid);
    }
  }
  return {fbp, fbc};
};

const installPixelStub = (): void => {
  if (typeof window === "undefined" || window.fbq) {
    return;
  }
  const fbq: Fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = PIXEL_SCRIPT;
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
};

const sendCapiEvent = (payload: {
  eventName: MetaEventName;
  eventId: string;
  email?: string;
  externalId?: string;
  customData?: CapiCustomData;
}): void => {
  if (isAndroid) {
    return;
  }
  const {fbp, fbc} = readClickIds();
  const sendCapi = httpsCallable<Record<string, unknown>, {success?: boolean}>(functions, "sendMetaCapiEvent");
  sendCapi({
    eventName: payload.eventName,
    eventId: payload.eventId,
    eventSourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
    eventTime: Math.floor(Date.now() / 1000),
    fbp,
    fbc,
    email: payload.email,
    externalId: payload.externalId,
    customData: payload.customData
  }).catch(() => {
    // Pixel already recorded the event; a CAPI miss must not break onboarding.
  });
};

const track = (
  eventName: MetaEventName,
  options: {
    customData?: CapiCustomData;
    email?: string;
    externalId?: string;
    onceKey?: string;
  } = {}
): void => {
  if (isAndroid || typeof window === "undefined" || !window.fbq) {
    return;
  }
  if (options.onceKey) {
    if (firedOnce.has(options.onceKey)) {
      return;
    }
    firedOnce.add(options.onceKey);
  }

  const eventId = IdGeneratorUtil.generateUuid();
  const pixelParams: Record<string, unknown> = {};
  if (typeof options.customData?.value === "number") {
    pixelParams.value = options.customData.value;
  }
  if (options.customData?.currency) {
    pixelParams.currency = options.customData.currency;
  }
  if (options.customData?.contentName) {
    pixelParams["content_name"] = options.customData.contentName;
  }
  if (options.customData?.contentCategory) {
    pixelParams["content_category"] = options.customData.contentCategory;
  }
  if (options.customData?.method) {
    pixelParams.method = options.customData.method;
  }

  const isCustom = eventName === META_CUSTOM_EVENTS.ONBOARDING_SELECTION;
  const eventIdOption = {eventID: eventId};
  if (isCustom) {
    window.fbq("trackCustom", eventName, pixelParams, eventIdOption);
  } else {
    window.fbq("track", eventName, pixelParams, eventIdOption);
  }
  sendCapiEvent({
    eventName,
    eventId,
    email: options.email,
    externalId: options.externalId,
    customData: options.customData
  });
};

/**
 * Loads the Meta Pixel on the web onboarding funnel and fires PageView (browser
 * + CAPI, same event_id so Meta can dedupe).
 */
export const initMetaPixel = (): void => {
  if (isAndroid || pixelReady || typeof window === "undefined") {
    return;
  }
  pixelReady = true;
  captureFbclid();
  installPixelStub();
  window.fbq("init", META_PIXEL_ID);
  track(META_STANDARD_EVENTS.PAGE_VIEW, {onceKey: "PageView"});
};

export const trackOnboardingSelection = (method?: string): void => {
  track(META_CUSTOM_EVENTS.ONBOARDING_SELECTION, {
    onceKey: method ? `onboarding_selection:${method}` : "onboarding_selection",
    customData: method ? {method, contentName: method} : undefined
  });
};

export const trackCompleteRegistration = (
  options: {email?: string; externalId?: string; method?: string} = {}
): void => {
  if (options.email && window.fbq) {
    window.fbq("init", META_PIXEL_ID, {em: options.email.trim().toLowerCase()});
  }
  track(META_STANDARD_EVENTS.COMPLETE_REGISTRATION, {
    onceKey: "CompleteRegistration",
    email: options.email,
    externalId: options.externalId,
    customData: {contentCategory: "onboarding", method: options.method}
  });
};

export const trackInitiateCheckout = (): void => {
  track(META_STANDARD_EVENTS.INITIATE_CHECKOUT, {
    onceKey: "InitiateCheckout",
    customData: {contentCategory: "subscription"}
  });
};

export const rememberCheckout = (checkout: RememberedCheckout): void => {
  safeStorage.set(CHECKOUT_STORAGE_KEY, JSON.stringify(checkout));
};

export const trackPurchase = (): void => {
  if (safeStorage.get(PURCHASE_FIRED_KEY)) {
    return;
  }
  safeStorage.set(PURCHASE_FIRED_KEY, "1");
  let checkout: RememberedCheckout = {value: 0, currency: "EUR"};
  const stored = safeStorage.get(CHECKOUT_STORAGE_KEY);
  if (stored) {
    try {
      checkout = {...checkout, ...JSON.parse(stored)};
    } catch {
      // Fall through with the trial-zero default.
    }
  }
  track(META_STANDARD_EVENTS.PURCHASE, {
    customData: {
      value: checkout.value,
      currency: checkout.currency,
      contentName: checkout.contentName,
      contentCategory: "subscription"
    }
  });
};
